import { Fragment, useMemo } from "react";
import { TriangleAlert } from "lucide-react";
import { redline, changesOnly } from "@/lib/redline";
import { plural } from "@/components/common";
import { cn } from "@/lib/utils";

// The Page view of a Markdown document's changes (design_handoff_documents_view/screens/Document Changes.dc.html): the
// document rendered as a page, added text green and underlined, removed text red and struck through, a bar in the
// margin by each changed paragraph. Everything is React text: nothing in the document becomes markup.

const TYPO = {
  h1: "font-display text-[22px] leading-[1.25] tracking-[0.02em] font-semibold",
  h2: "font-display text-[15px] leading-[22px] tracking-[0.02em] font-semibold pt-1.5",
  p: "leading-[22px] text-gr-ink-secondary [text-wrap:pretty]",
  li: "leading-[22px] text-gr-ink-secondary",
  quote: "leading-[22px] text-gr-ink-secondary italic border-s-2 border-gr-hairline ps-3",
  code: "font-mono text-[12px] leading-5 whitespace-pre-wrap bg-gr-surface-2 rounded-md px-3 py-2",
  tr: "leading-[22px] text-gr-ink-secondary border-b border-gr-hairline pb-1",
};
const typo = (t) => TYPO[t] || (/^h\d$/.test(t) ? "font-semibold pt-1" : TYPO.p);
const STYLE = { b: "font-semibold text-foreground", i: "italic", code: "font-mono text-[0.92em]", link: "underline decoration-dotted underline-offset-2" };
const BAR = { add: "bg-[var(--gr-redline-add)]", del: "bg-[var(--gr-redline-del)]", mod: "bg-gr-warning" };
const ADD = "text-[var(--gr-redline-add)] bg-[var(--gr-redline-add-bg)] underline decoration-[var(--gr-redline-add)] underline-offset-[3px] rounded-sm px-px";
const DEL = "text-[var(--gr-redline-del)] bg-[var(--gr-redline-del-bg)] line-through decoration-[var(--gr-redline-del)] rounded-sm px-px";

function Seg({ s }) {
  const inner = s.s ? <span className={STYLE[s.s]}>{s.t}</span> : s.t;
  if (s.k === "add") return <ins className={ADD}>{inner}</ins>;
  if (s.k === "del") return <del className={DEL}>{inner}</del>;
  return <>{inner}</>;
}

function Block({ b }) {
  if (b.type === "hr") return <hr className="border-gr-hairline my-1" />;
  return (
    <div className={cn("relative ps-4 -ms-4", b.context && "opacity-60")} data-change={b.change}>
      {BAR[b.change] && <span aria-hidden="true" className={cn("absolute start-0 top-[3px] bottom-[3px] w-0.5 rounded-sm", BAR[b.change])} />}
      <div className={cn(typo(b.type), b.type === "tr" && b.header && "font-semibold text-foreground", b.type === "li" && "flex gap-2")}
        style={b.type === "li" ? { paddingInlineStart: `${b.level * 18}px` } : undefined}>
        {b.type === "li" && <span aria-hidden="true" className="shrink-0 text-muted-foreground">{b.ordered ? b.marker : "•"}</span>}
        <span className="min-w-0 [overflow-wrap:anywhere]">{b.segs.map((s, i) => <Seg key={i} s={s} />)}</span>
      </div>
    </div>
  );
}

export function RedlineLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground" data-testid="redline-legend">
      <ins className={ADD}>Added text</ins>
      <del className={DEL}>Removed text</del>
      <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="w-0.5 h-3.5 rounded-sm bg-gr-warning" />A bar marks each changed paragraph</span>
    </div>
  );
}

// `page`: GET /sessions/{id}/diff/page ({status, old, new, truncated}); `onlyChanges`: the "Changes only" cut
export default function RedlinePage({ page, onlyChanges, name }) {
  const blocks = useMemo(() => redline(page.old, page.new), [page.old, page.new]);
  const items = onlyChanges && page.status === "modified" ? changesOnly(blocks) : blocks;
  return (
    <div className="flex flex-col gap-3">
      {page.truncated && (
        <div role="note" className="flex gap-2 px-3.5 py-2.5 border rounded-lg text-sm max-w-[720px]" data-testid="page-truncated">
          <TriangleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gr-warning" />
          <span className="text-gr-ink-secondary [text-wrap:pretty]">This document is too large to show in full (over 512 KB). Here is the first part. Every change is still saved in the version and goes into the change report.</span>
        </div>
      )}
      <article aria-label={`${name}, with its changes marked`} className="bg-card rounded-[10px] px-5 py-5 sm:px-9 sm:py-7 max-w-[720px] w-full flex flex-col gap-3" data-testid="redline-page">
        {items.map((b, i) => b.gap ? (
          <div key={`g${i}`} className="flex items-center gap-2.5 text-sm text-muted-foreground">
            <span className="flex-1 h-px bg-gr-hairline" /><span>{plural(b.gap, "unchanged paragraph")}</span><span className="flex-1 h-px bg-gr-hairline" />
          </div>
        ) : <Fragment key={i}><Block b={b} /></Fragment>)}
        {!items.length && <span className="text-muted-foreground">This document is empty.</span>}
        {page.truncated && <span className="text-sm text-muted-foreground">The rest of this document isn't shown.</span>}
      </article>
    </div>
  );
}
